import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Group,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { Clock, Target, X, Copy, ChevronDown } from 'react-feather';
// react-feather has no pin glyph; use lucide's Pin for the "Pin" action (Brook).
import { Pin } from 'lucide-react';
import {
  nudgeCategory,
  type NudgeCategory,
  type SessionNudge,
} from '../../../../hooks/useSessionNudges';
import { ContentCard } from '../../../../components/common/ContentCard';

/**
 * MED-872 / 2.3b AC5 + AC6 — right-column "AI Insights" panel.
 * MED-1017 — Live Session Polishing: matched to Figma 2044:17212.
 * MED-1167 / 2.3g — Pin & New/Pinned triage.
 *
 * Triage model (MED-1167): the panel is no longer a flat history. Insights the
 * leader hasn't handled sit in a **New** inbox (with Live/Pace category views of
 * that same unhandled set); an explicit **Pin** action moves a card to the
 * separate **Pinned** bucket (kept for this session only — no persistence). Each
 * card carries a single split footer — constructive actions grouped left, the
 * lone Dismiss set apart on the right (the corner X is gone, so the footer
 * Dismiss is the only remove affordance for every card type).
 *
 * Footer (post-client review — Brook/Michelle): Copy · Pin · Dismiss. Copy grabs
 * the WHOLE insight (message + suggested prompt when present) and shows on every
 * keepable card, so it's the single "get the text out" action (the separate
 * "Add to notes" button was dropped — copy and paste into notes instead). PACE
 * stays Dismiss-only.
 *
 * AI-generated content is color-coded: LIVE purple, PACE amber, PRE-WORK blue
 * (AC2). Nudges render as full-bleed rows with a colored accent bar; pacing
 * nudges persist (require dismiss) — the toast layer (SessionNudgeToasts) owns
 * auto-dismiss for the ephemeral surfacing.
 *
 * The panel is shared, so this behavior applies to the peer-leader active
 * session view (E4.6) as well as the coach view (MED-1167 peer-leader parity).
 */

type InsightTab = 'new' | 'live' | 'pace' | 'pre_work' | 'pinned';

/**
 * MED-798 — a transcript line within this window means the bot is actively
 * hearing the room. Generous enough to span natural conversational pauses, so
 * a brief silence doesn't flip the indicator; long enough that a bot that
 * stopped feeding audio surfaces as "Waiting for audio…" within ~half a minute.
 */
const TRANSCRIPT_FRESH_MS = 30_000;

/**
 * MED-1276 — how long the AI may go without COMPLETING an analysis before the
 * panel stops claiming it's listening. session-intelligence debounces inference
 * to one call per DEBOUNCE_SECONDS (30s), so a healthy loop over a talking room
 * lands a check every ~30-60s. 3 minutes is several times that: long enough to
 * ride out a lull or a slow model call, short enough that a stalled analysis
 * loop surfaces well inside a session instead of after it.
 *
 * This is the honest-signal half of the heartbeat (MED-1169 shipped the
 * counting-up timestamp). On 2026-07-29 a 20-minute session sat under a green
 * "Listening active" with a silent panel and no heartbeat at all, and the
 * leader read it as a Zoom bug — a quiet panel has to distinguish "watching and
 * fine" from "not watching".
 */
const CHECK_STALE_MS = 180_000;

export interface ActiveSessionInsightsPanelProps {
  /** Newest-first; may include dismissed nudges (filtered out of the panel). */
  nudges: SessionNudge[];
  highlightedNudgeIds: Set<string>;
  /** Connected to session-intelligence — drives the "Listening active" state. */
  isConnected: boolean;
  /** MED-953 — coach paused AI listening; header + empty state must say so. */
  aiPaused?: boolean;
  /**
   * MED-798 — ms epoch of the last transcript line, or null if none yet. A
   * connected socket alone never meant audio was flowing; this distinguishes
   * "hearing the room" from "connected but the bot isn't feeding audio" (e.g.
   * a Recall bot stuck in the Google Meet waiting room).
   */
  lastTranscriptAt?: number | null;
  /**
   * MED-1169 — ms epoch of the AI's last COMPLETED analysis (null before the
   * first). Drives the heartbeat line in the quiet panel: proof the AI is not
   * just hearing the room but actually reading it. Deliberately allowed to age
   * visibly — that aging IS the tell that the analysis loop is down, and past
   * CHECK_STALE_MS it also flips the header out of "Listening active" (MED-1276).
   */
  lastCheckAt?: number | null;
  /** MED-1169 — decision of that analysis; 'none' earns the "nothing to flag" suffix. */
  lastCheckDecision?: 'nudge' | 'none' | null;
  /**
   * MED-1276 — ms epoch from which a completed analysis is OVERDUE (the first
   * transcript line of the current listening run; null while no audio is
   * flowing). Lets the panel call out a stalled analysis loop even before the
   * first check ever lands — without it, "the AI has never checked" is
   * indistinguishable from "the AI is about to check".
   */
  checksDueSince?: number | null;
  /** ms epoch ticker from the page so relative timestamps stay fresh. */
  nowMs: number;
  onDismiss: (id: string) => void;
  /** MED-1167 — Pin moves the whole card to the Pinned tab (in-session only). */
  onPin: (id: string) => void;
  /** MED-1167 — copy the whole insight (message + prompt) to the clipboard. */
  onCopy: (text: string) => void;
  /** DEV-only QA affordance to inject a nudge of each category. */
  onDemoNudge?: (category: NudgeCategory) => void;
}

// Figma 2044:17212 category treatments.
const CATEGORY_META: Record<
  NudgeCategory,
  { label: string; color: string; rowBg?: string }
> = {
  live: { label: 'LIVE', color: '#8B5CB8' },
  pace: { label: 'PACE', color: '#E07B4C', rowBg: '#FFFBF7' },
  pre_work: { label: 'PRE-WORK', color: '#1D4ED8' },
};

// Recipe v5 — live nudges carry the dimension the AI actually observed; show it
// after the LIVE tag ("LIVE · BALANCE VOICES"). topic_alignment keeps the bare
// LIVE tag (pre-v5 look), as does any subtype not listed here.
const LIVE_SUBTYPE_LABEL: Partial<Record<SessionNudge['subtype'], string>> = {
  rebalance_speaking: 'Balance voices',
  reflect_silence: 'Silence',
  check_understanding: 'Check understanding',
  missed_coaching_opportunity: 'Coaching opportunity',
  ambient_read: 'Read of the room',
};

const ROW_DIVIDER = '#F3F4F6';
const MSG_COLOR = '#374151';
const TS_COLOR = '#9CA3AF';
const TAB_DIVIDER = '#E5E7EB';

// MED-1167 footer role colors. Pin = soft primary (navy tint, matching the
// selected-tab treatment); Dismiss = restrained "remove" red (muted, not
// alarm-red); Copy stays neutral so Pin reads as the primary keep action.
const PIN_BG = '#EBF2FA';
const PIN_BORDER = '#B0CCE0';
const PIN_FG = '#203358';
const DISMISS_BG = '#FCF1F0';
const DISMISS_FG = '#B4443C';
const DISMISS_BORDER = '#E3B7B3';

// MED-1167 — tighten the leftSection→label gap on the labeled footer buttons
// (Mantine's default section spacing reads as too loose at this size).
const LABELED_BUTTON_SECTION = { marginInlineEnd: 5 };

// MED-1167 footer tooltips describe the behavior, not just the label (AC).
const TOOLTIP = {
  copy: 'Copy to clipboard',
  pin: 'Keep handy for this session',
  dismiss: 'Remove from your list',
};

function CategoryIcon({ category }: { category: NudgeCategory }) {
  const { color } = CATEGORY_META[category];
  if (category === 'pace') return <Clock size={14} color={color} />;
  if (category === 'pre_work') return <Target size={14} color={color} />;
  // LIVE — AI sparkle (the app's AI motif throughout).
  return (
    <Text
      component="span"
      aria-hidden="true"
      style={{ fontSize: 13, lineHeight: 1 }}
    >
      ✨
    </Text>
  );
}

/** "Just now" / "N min ago" / "N hr ago" from an ISO timestamp. */
function formatRelative(iso: string, nowMs: number): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const sec = Math.max(0, Math.floor((nowMs - then) / 1000));
  if (sec < 45) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  return `${hr} hr ago`;
}

/**
 * MED-1169 — seconds-first relative time for the heartbeat line (lowercase,
 * in-sentence). Unlike formatRelative's 45s "Just now" floor (right for card
 * timestamps), the heartbeat ticks per-second — visible aging IS the liveness
 * signal on a loop that completes an analysis every ~30-60s of conversation.
 */
function formatHeartbeat(atMs: number, nowMs: number): string {
  const sec = Math.max(0, Math.floor((nowMs - atMs) / 1000));
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  return `${Math.floor(min / 60)} hr ago`;
}

/**
 * MED-1167 — the per-card action footer, on every card type. A single row split
 * into a constructive group (left) and the lone Dismiss (right, mis-tap
 * protection). Composition by type (post-client review):
 *   - LIVE (w/ or w/o prompt) · PRE-WORK: Copy · Pin · Dismiss
 *   - PACE: Dismiss only
 * Pinned cards drop Pin (already pinned). Copy grabs the whole insight (message
 * + suggested prompt when present) and shows on every keepable card.
 *
 * MED-1246 — the PACE "Advance to next step" link was removed here. It advanced
 * from the leader's current section rather than the section the prompt named,
 * so on a stale prompt it pushed the leader past their current section. Section
 * advancement now lives solely on the top-band "Next"/nav control; the panel
 * pacing card clears only via its own Dismiss (dropping the former MED-1167
 * advance-and-auto-clear).
 */
function NudgeCardFooter({
  nudge,
  category,
  onCopy,
  onPin,
  onDismiss,
}: {
  nudge: SessionNudge;
  category: NudgeCategory;
  onCopy: (text: string) => void;
  onPin: (id: string) => void;
  onDismiss: (id: string) => void;
}) {
  const isPace = category === 'pace';
  // PACE cards are act-or-clear time reminders — no keep actions (AC).
  const canKeep = !isPace;
  const showPin = canKeep && !nudge.pinned;
  // Copy grabs the whole insight — message, plus the suggested prompt in quotes
  // when the card carries one — so a paste lands the full context anywhere.
  const copyText = nudge.source
    ? `${nudge.message}\n\n“${nudge.source}”`
    : nudge.message;

  return (
    <Group
      justify="space-between"
      align="center"
      wrap="nowrap"
      gap={8}
      mt={6}
      data-testid="active-session-insight-footer"
    >
      {/* Constructive actions — grouped left */}
      <Group gap={6} align="center" wrap="nowrap">
        {canKeep && (
          <Tooltip label={TOOLTIP.copy} withArrow>
            <ActionIcon
              size={28}
              radius={6}
              variant="default"
              aria-label={TOOLTIP.copy}
              data-testid="active-session-insight-copy"
              onClick={() => onCopy(copyText)}
            >
              <Copy size={14} />
            </ActionIcon>
          </Tooltip>
        )}
        {showPin && (
          <Tooltip label={TOOLTIP.pin} withArrow>
            <Button
              size="compact-xs"
              radius={6}
              variant="default"
              leftSection={<Pin size={12} />}
              data-testid="active-session-insight-pin"
              onClick={() => onPin(nudge.id)}
              styles={{
                // Match the 28px icon buttons so the footer row is even.
                root: {
                  height: 28,
                  background: PIN_BG,
                  borderColor: PIN_BORDER,
                  color: PIN_FG,
                },
                section: LABELED_BUTTON_SECTION,
              }}
            >
              Pin
            </Button>
          </Tooltip>
        )}
      </Group>

      {/* Dismiss — the only remove action, set apart on the right */}
      <Tooltip label={TOOLTIP.dismiss} withArrow>
        <Button
          size="compact-xs"
          radius={6}
          variant="default"
          leftSection={<X size={12} />}
          aria-label="Dismiss"
          data-testid="active-session-insight-dismiss"
          onClick={() => onDismiss(nudge.id)}
          styles={{
            // Match the 28px icon buttons so the footer row is even.
            root: {
              height: 28,
              background: DISMISS_BG,
              borderColor: DISMISS_BORDER,
              color: DISMISS_FG,
            },
            section: LABELED_BUTTON_SECTION,
          }}
        >
          Dismiss
        </Button>
      </Tooltip>
    </Group>
  );
}

export function ActiveSessionInsightsPanel({
  nudges,
  highlightedNudgeIds,
  isConnected,
  aiPaused = false,
  lastTranscriptAt = null,
  lastCheckAt = null,
  lastCheckDecision = null,
  checksDueSince = null,
  nowMs,
  onDismiss,
  onPin,
  onCopy,
  onDemoNudge,
}: ActiveSessionInsightsPanelProps) {
  const [tab, setTab] = useState<InsightTab>('new');

  // MED-1167 — the unhandled inbox (New / Live / Pace views) vs. the separate
  // Pinned bucket. Both exclude dismissed; New excludes pinned, Pinned requires it.
  const unhandled = useMemo(
    () => nudges.filter(n => !n.dismissed && !n.pinned),
    [nudges]
  );
  const pinnedList = useMemo(
    () => nudges.filter(n => !n.dismissed && n.pinned),
    [nudges]
  );

  const counts = useMemo(() => {
    const c = {
      new: unhandled.length,
      live: 0,
      pace: 0,
      pre_work: 0,
      pinned: pinnedList.length,
    };
    for (const n of unhandled) c[nudgeCategory(n)] += 1;
    return c;
  }, [unhandled, pinnedList]);

  // Pre-work tab is a placeholder — only surfaces once pre-session nudges exist (AC5).
  const showPreWorkTab = counts.pre_work > 0;

  const visible = useMemo(() => {
    if (tab === 'pinned') return pinnedList;
    if (tab === 'new') return unhandled;
    return unhandled.filter(n => nudgeCategory(n) === tab);
  }, [tab, unhandled, pinnedList]);

  // MED-1280 (AC5) — the list is newest-first, so a fresh insight pushes older
  // ones DOWN and, in a short panel (worst in the three-panel rail), below the
  // fold. The default hover-only scrollbar leaves no cue they're there, so a
  // still-outstanding insight reads as if it were removed — the reported defect.
  // Measure the scroll viewport and surface an explicit "more" affordance in
  // either direction, so overflow is evident from the panel itself, not
  // something the leader has to discover by scrolling.
  const viewportRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState({ top: false, bottom: false });

  const measureOverflow = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const EPS = 2; // sub-pixel rounding guard so a fully-scrolled list reads flush
    const { scrollTop, scrollHeight, clientHeight } = el;
    setOverflow(prev => {
      const next = {
        top: scrollTop > EPS,
        bottom: scrollHeight - clientHeight - scrollTop > EPS,
      };
      return prev.top === next.top && prev.bottom === next.bottom ? prev : next;
    });
  }, []);

  // Re-measure whenever the rendered list or tab changes (an arriving/dismissed
  // insight changes the content height) and whenever the viewport is resized
  // (opening Notes/Participants shrinks this panel).
  useEffect(() => {
    measureOverflow();
    const el = viewportRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measureOverflow);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureOverflow, visible, tab]);

  const scrollToBottom = useCallback(() => {
    const el = viewportRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, []);

  // The three unhandled views group together; Held is a separate axis, rendered
  // after a divider at the end of the tab row.
  const unhandledTabs: Array<{
    key: InsightTab;
    label: string;
    count: number;
  }> = [
    { key: 'new', label: 'New', count: counts.new },
    { key: 'live', label: 'Live', count: counts.live },
    { key: 'pace', label: 'Pace', count: counts.pace },
    ...(showPreWorkTab
      ? [
          {
            key: 'pre_work' as InsightTab,
            label: 'Pre-work',
            count: counts.pre_work,
          },
        ]
      : []),
  ];

  // MED-798 — "hearing the room" requires a transcript to have arrived recently,
  // not just a connected socket. If the bot never joined / was never admitted to
  // the meeting, no transcript ever arrives and the indicator honestly reads
  // "Waiting for audio…" instead of a green "Listening active" that lies.
  const isHearingRoom =
    lastTranscriptAt != null && nowMs - lastTranscriptAt < TRANSCRIPT_FRESH_MS;

  // MED-1276 — a check is only OWED while we're connected, unpaused, and
  // hearing the room. Nothing is analyzed otherwise, so nothing can be late:
  // without this gate a 5-minute pause would render as a stalled analysis.
  const checksOwed = isConnected && !aiPaused && isHearingRoom;
  // The clock staleness runs against: the last COMPLETED analysis, or (before
  // the first one lands) the moment analyses became due. Whichever is newer, so
  // resuming after a pause restarts the clock instead of inheriting the gap the
  // pause itself created.
  const checkClock = Math.max(lastCheckAt ?? 0, checksDueSince ?? 0);
  const isCheckStale =
    checksOwed && checkClock > 0 && nowMs - checkClock > CHECK_STALE_MS;

  // 'stale' sits BELOW 'waiting': with no fresh transcript the bot isn't
  // feeding audio, so "Waiting for audio…" is the truer read. Only a room we're
  // demonstrably hearing can stall the analysis.
  const listeningState:
    | 'connecting'
    | 'paused'
    | 'waiting'
    | 'stale'
    | 'active' = !isConnected
    ? 'connecting'
    : aiPaused
      ? 'paused'
      : !isHearingRoom
        ? 'waiting'
        : isCheckStale
          ? 'stale'
          : 'active';
  const statusText =
    listeningState === 'connecting'
      ? 'Connecting…'
      : listeningState === 'paused'
        ? 'Paused — not listening'
        : listeningState === 'waiting'
          ? 'Waiting for audio…'
          : listeningState === 'stale'
            ? 'Analysis stalled'
            : 'Listening active';
  const statusColor =
    listeningState === 'active'
      ? 'medleyGreen'
      : listeningState === 'waiting'
        ? 'warningAmber'
        : listeningState === 'stale'
          ? 'dangerRed'
          : 'dimmed';
  const statusDotBg =
    listeningState === 'active'
      ? 'var(--mantine-color-medleyGreen-6)'
      : listeningState === 'waiting'
        ? 'var(--mantine-color-warningAmber-5)'
        : listeningState === 'stale'
          ? 'var(--mantine-color-dangerRed-5)'
          : 'var(--mantine-color-gray-4)';

  const renderTab = (t: { key: InsightTab; label: string; count: number }) => {
    const selected = tab === t.key;
    return (
      <UnstyledButton
        key={t.key}
        onClick={() => setTab(t.key)}
        data-testid={`active-session-insights-tab-${t.key}`}
        data-selected={selected ? 'true' : 'false'}
        style={{
          display: 'inline-flex',
          alignItems: 'baseline',
          gap: 3,
          padding: '5px 11px',
          borderRadius: 4,
          border: `1px solid ${selected ? '#B0CCE0' : 'transparent'}`,
          background: selected ? '#EBF2FA' : 'transparent',
        }}
      >
        <Text
          component="span"
          fz={11}
          fw={selected ? 600 : 400}
          c={selected ? '#203358' : '#6B7280'}
        >
          {t.label}
        </Text>
        <Text
          component="span"
          fz={9}
          fw={600}
          c={selected ? '#203358' : '#6B7280'}
        >
          {t.count}
        </Text>
      </UnstyledButton>
    );
  };

  return (
    <ContentCard
      data-testid="active-session-insights-panel"
      flushContent
      // MED-1280 (AC5) — keep a usable floor so, when Notes and Participants
      // share the rail and squeeze this card, the list can't collapse to ~one
      // card tall (the reachability worst case). The rail Stack scrolls instead.
      style={{ minHeight: 280 }}
      header={
        <Group justify="space-between" align="center" wrap="nowrap" w="100%">
          <Group gap={12} align="center" wrap="nowrap" style={{ minWidth: 0 }}>
            <Group gap={6} align="center" wrap="nowrap">
              <Text
                component="span"
                aria-hidden="true"
                style={{
                  fontSize: 14,
                  lineHeight: 1,
                  position: 'relative',
                  top: -1,
                }}
              >
                ✨
              </Text>
              <Text fz={12} fw={600} c="#1A1A2E">
                AI Insights
              </Text>
            </Group>
            <Group gap={6} align="center" wrap="nowrap">
              <Box
                data-testid="active-session-insights-listening-dot"
                // MED-1169 — gentle breathe only while actively hearing the
                // room: an ambient "alive" signal that promises nothing (the
                // honest alternative to a fake "thinking…" indicator). Static
                // in every other state; disabled under prefers-reduced-motion.
                className={
                  listeningState === 'active'
                    ? 'medley-listening-pulse'
                    : undefined
                }
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  background: statusDotBg,
                }}
              />
              <Text
                fz={11}
                c={statusColor}
                data-testid="active-session-insights-listening-label"
              >
                {statusText}
              </Text>
            </Group>
          </Group>
          {import.meta.env.DEV && onDemoNudge && (
            <Tooltip label="DEV: inject demo nudges">
              <Group gap={2}>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="surveyPurple"
                  onClick={() => onDemoNudge('live')}
                >
                  <span style={{ fontSize: 11 }}>L</span>
                </ActionIcon>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="warningAmber"
                  onClick={() => onDemoNudge('pace')}
                >
                  <span style={{ fontSize: 11 }}>P</span>
                </ActionIcon>
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="blue"
                  onClick={() => onDemoNudge('pre_work')}
                >
                  <span style={{ fontSize: 11 }}>W</span>
                </ActionIcon>
              </Group>
            </Tooltip>
          )}
        </Group>
      }
    >
      {/* Tabs — New · Live · Pace grouped, a divider, then the separate Pinned bucket */}
      <Group
        gap={4}
        wrap="nowrap"
        align="center"
        data-testid="active-session-insights-tabs"
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${TAB_DIVIDER}`,
        }}
      >
        {unhandledTabs.map(renderTab)}
        <Box
          aria-hidden="true"
          style={{
            width: 1,
            alignSelf: 'stretch',
            margin: '2px 6px',
            background: TAB_DIVIDER,
          }}
        />
        {renderTab({ key: 'pinned', label: 'Pinned', count: counts.pinned })}
      </Group>

      {/* Nudge history — MED-1280 AC5: overflow must be evident from the panel
          itself. Always-when-needed scrollbar (house convention) + directional
          fade/"more" cues so an off-screen insight isn't read as a removed one. */}
      <Box
        style={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ScrollArea
          viewportRef={viewportRef}
          onScrollPositionChange={measureOverflow}
          scrollbarSize={8}
          type="auto"
          offsetScrollbars={false}
          style={{ flex: 1, minHeight: 0 }}
        >
          {visible.length === 0 ? (
            <Stack align="center" gap={6} py="xl" px="md">
              <Box style={{ opacity: 0.3, fontSize: 32 }} aria-hidden="true">
                ✨
              </Box>
              <Text size="sm" c="dimmed" ta="center" maw={260}>
                {tab === 'pinned'
                  ? 'Insights you pin will collect here for this session.'
                  : !isConnected
                    ? 'Connecting to the session intelligence service…'
                    : aiPaused
                      ? 'AI listening is paused — no new insights until you resume.'
                      : listeningState === 'waiting'
                        ? // No fresh audio (bot not admitted, or a long lull): the
                          // header says "Waiting for audio…", so the body must not
                          // claim the conversation is flowing.
                          'Insights appear at key moments — when the group stalls, one voice dominates, or someone goes quiet.'
                        : listeningState === 'stale'
                          ? // MED-1276 — audio IS flowing but no analysis has
                            // completed in a long while. The reassuring "quiet
                            // panel = flowing conversation" reading is exactly
                            // what must not be offered here: silence is no longer
                            // evidence of anything.
                            'The AI is hearing the room but hasn’t completed a check recently — a quiet panel may not mean a quiet conversation.'
                          : 'Insights appear at key moments — when the group stalls, one voice dominates, or someone goes quiet. A quiet panel usually means the conversation’s flowing.'}
              </Text>
              {/* MED-1169 — the liveness heartbeat: proof the AI is not just
                hearing the room but actually reading it, and that silence is a
                choice. Past-tense, so it stays literally true in every state
                (paused, reconnecting, transcript-stale); the "nothing to flag"
                suffix shows ONLY when the last completed analysis literally
                decided 'none' — it must never contradict a card the leader
                just dismissed or pinned. Hidden on the Pinned tab (its empty
                copy is about the Pinned bucket, not liveness). */}
              {/* MED-1276 — it now ALSO renders before the first check ever
                lands, but only while one is owed: "waiting for the first check"
                is a promise the panel can't keep while paused or while the bot
                isn't feeding audio. This is the line whose absence let a
                20-minute silent session read as "watching and fine". */}
              {tab !== 'pinned' && (lastCheckAt != null || checksOwed) && (
                <Text
                  fz={11}
                  c={isCheckStale ? 'dangerRed' : 'dimmed'}
                  ta="center"
                  data-testid="active-session-insights-heartbeat"
                  data-stale={isCheckStale ? 'true' : 'false'}
                >
                  {/* The "Nothing to flag" suffix additionally requires an EMPTY
                    inbox: a category tab can be empty while an unhandled card
                    sits one tab over, and "Nothing to flag" under a New(1)
                    badge reads as a contradiction even when literally true of
                    the last check. MED-1276 withholds it once the check is
                    stale too — a stale "nothing to flag" is exactly the false
                    reassurance the 2026-07-29 session ran on. */}
                  {lastCheckAt == null
                    ? isCheckStale
                      ? 'No analysis has completed yet — the AI may not be reading the conversation.'
                      : 'Waiting for the AI’s first check…'
                    : isCheckStale
                      ? `Last checked the conversation ${formatHeartbeat(lastCheckAt, nowMs)} — analysis may have stopped.`
                      : lastCheckDecision === 'none' && unhandled.length === 0
                        ? `Nothing to flag — last checked the conversation ${formatHeartbeat(lastCheckAt, nowMs)}`
                        : `Last checked the conversation ${formatHeartbeat(lastCheckAt, nowMs)}`}
                </Text>
              )}
            </Stack>
          ) : (
            visible.map(nudge => {
              const category = nudgeCategory(nudge);
              const meta = CATEGORY_META[category];
              const subtypeLabel =
                category === 'live'
                  ? LIVE_SUBTYPE_LABEL[nudge.subtype]
                  : undefined;
              const highlighted = highlightedNudgeIds.has(nudge.id);
              const hasPrompt = category === 'live' && !!nudge.source;
              return (
                <Box
                  key={nudge.id}
                  data-testid="active-session-insight-card"
                  data-category={category}
                  style={{
                    position: 'relative',
                    display: 'flex',
                    gap: 10,
                    alignItems: 'flex-start',
                    padding: '14px 20px 15px',
                    borderBottom: `1px solid ${ROW_DIVIDER}`,
                    background: highlighted
                      ? 'var(--mantine-color-surveyPurple-0)'
                      : (meta.rowBg ?? undefined),
                    transition: 'background 0.4s ease',
                  }}
                >
                  {/* Accent bar */}
                  <Box
                    aria-hidden="true"
                    style={{
                      alignSelf: 'stretch',
                      width: 4,
                      flexShrink: 0,
                      borderRadius: 2,
                      background: meta.color,
                    }}
                  />
                  {/* Category icon */}
                  <Box style={{ flexShrink: 0, lineHeight: 0, paddingTop: 1 }}>
                    <CategoryIcon category={category} />
                  </Box>
                  {/* Content */}
                  <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                    <Group
                      justify="space-between"
                      align="baseline"
                      wrap="nowrap"
                    >
                      <Text
                        fz={10}
                        fw={700}
                        c={meta.color}
                        tt="uppercase"
                        style={{ letterSpacing: '0.4px' }}
                      >
                        {subtypeLabel
                          ? `${meta.label} · ${subtypeLabel}`
                          : meta.label}
                      </Text>
                      <Text fz={11} c={TS_COLOR} style={{ flexShrink: 0 }}>
                        {formatRelative(nudge.timestamp, nowMs)}
                      </Text>
                    </Group>

                    <Text fz={13} fw={500} c={MSG_COLOR} lh={1.5}>
                      {nudge.message}
                    </Text>

                    {hasPrompt && (
                      <Box
                        data-testid="active-session-insight-suggested-prompt"
                        style={{
                          background: '#F8FAFB',
                          border: '1px solid #8B5CB8',
                          borderRadius: 8,
                          padding: '14px 13px 13px',
                        }}
                      >
                        <Group gap={4} align="center" wrap="nowrap" mb={4}>
                          <Text
                            component="span"
                            aria-hidden="true"
                            style={{ fontSize: 12, lineHeight: 1 }}
                          >
                            ✨
                          </Text>
                          <Text
                            fz={10}
                            fw={700}
                            c="#8B5CB8"
                            tt="uppercase"
                            style={{ letterSpacing: '0.4px' }}
                          >
                            Suggested prompt
                          </Text>
                        </Group>
                        <Text
                          fz={13}
                          fw={500}
                          fs="italic"
                          c={MSG_COLOR}
                          lh={1.5}
                        >
                          “{nudge.source}”
                        </Text>
                      </Box>
                    )}

                    <NudgeCardFooter
                      nudge={nudge}
                      category={category}
                      onCopy={onCopy}
                      onPin={onPin}
                      onDismiss={onDismiss}
                    />
                  </Stack>
                </Box>
              );
            })
          )}
        </ScrollArea>

        {/* AC5 — earlier insights scrolled above the fold: a non-interactive
            "there's more up here" cue. */}
        {overflow.top && (
          <Box
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 24,
              pointerEvents: 'none',
              background: 'linear-gradient(to bottom, #fff, transparent)',
            }}
          />
        )}

        {/* AC5 — the reported case: outstanding insights sitting past the fold.
            A fade signals more at a glance; the chip reveals them on tap. */}
        {overflow.bottom && (
          <>
            <Box
              aria-hidden="true"
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 32,
                pointerEvents: 'none',
                background: 'linear-gradient(to top, #fff, transparent)',
              }}
            />
            <UnstyledButton
              onClick={scrollToBottom}
              data-testid="active-session-insights-more-below"
              style={{
                position: 'absolute',
                bottom: 8,
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '3px 10px',
                borderRadius: 999,
                border: '1px solid #B0CCE0',
                background: '#EBF2FA',
                color: '#203358',
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              }}
            >
              <Text component="span" fz={10} fw={600}>
                More below
              </Text>
              <ChevronDown size={12} />
            </UnstyledButton>
          </>
        )}
      </Box>
    </ContentCard>
  );
}
